import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Users,
  Plus, 
  Trash2,
  Search,
  Edit,
  Mail,
  Phone,
  MessageSquare,
  FileSignature,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  ExternalLink,
  MoreVertical,
  Star,
  Building2,
  Handshake,
  Briefcase,
  Newspaper,
  User,
  Send,
  Clock,
  CheckCircle,
  Copy,
  Check,
  Share2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SocialAccountsManager } from "../components/SocialAccountsManager";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AccountsPage = ({ user, logout, token }) => {
  // State
  const [accounts, setAccounts] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactRoles, setContactRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Dialog states
  const [addSignatureOpen, setAddSignatureOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactMessages, setContactMessages] = useState([]);
  
  // Form states
  const [newSignature, setNewSignature] = useState({
    name: "",
    content: "",
    platforms: [],
    is_default: false,
    account_id: ""
  });
  
  const [newContact, setNewContact] = useState({
    name: "",
    role: "collaborator",
    email: "",
    phone: "",
    social_handles: {},
    notes: "",
    tags: "",
    account_ids: []
  });
  
  const [newMessage, setNewMessage] = useState({
    message: "",
    channel: "note"
  });

  const platformOptions = [
    { id: "twitter", name: "Twitter", icon: <Twitter className="w-4 h-4" />, color: "bg-blue-500" },
    { id: "instagram", name: "Instagram", icon: <Instagram className="w-4 h-4" />, color: "bg-pink-500" },
    { id: "facebook", name: "Facebook", icon: <Facebook className="w-4 h-4" />, color: "bg-indigo-500" },
    { id: "linkedin", name: "LinkedIn", icon: <Linkedin className="w-4 h-4" />, color: "bg-sky-500" },
    { id: "tiktok", name: "TikTok", icon: <span className="text-xs font-bold">TT</span>, color: "bg-gray-800" },
    { id: "youtube", name: "YouTube", icon: <Youtube className="w-4 h-4" />, color: "bg-red-500" }
  ];

  const roleIcons = {
    influencer: <Star className="w-4 h-4" />,
    brand: <Building2 className="w-4 h-4" />,
    collaborator: <Handshake className="w-4 h-4" />,
    agency: <Briefcase className="w-4 h-4" />,
    client: <Briefcase className="w-4 h-4" />,
    media: <Newspaper className="w-4 h-4" />,
    other: <User className="w-4 h-4" />
  };

  const roleColors = {
    influencer: "bg-yellow-100 text-yellow-700",
    brand: "bg-blue-100 text-blue-700",
    collaborator: "bg-green-100 text-green-700",
    agency: "bg-purple-100 text-purple-700",
    client: "bg-orange-100 text-orange-700",
    media: "bg-pink-100 text-pink-700",
    other: "bg-gray-100 text-gray-700"
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [accountsRes, signaturesRes, contactsRes, rolesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/social`, { headers }),
        axios.get(`${BACKEND_URL}/api/signatures`, { headers }),
        axios.get(`${BACKEND_URL}/api/contacts`, { headers }),
        axios.get(`${BACKEND_URL}/api/contacts/roles`, { headers })
      ]);
      
      setAccounts(accountsRes.data);
      setSignatures(signaturesRes.data);
      setContacts(contactsRes.data);
      setContactRoles(rolesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Signature functions
  const handleAddSignature = async () => {
    if (!newSignature.name.trim() || !newSignature.content.trim()) {
      toast.error("Nombre y contenido son requeridos");
      return;
    }
    if (newSignature.platforms.length === 0) {
      toast.error("Selecciona al menos una plataforma");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/signatures`,
        newSignature,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Firma creada");
      setAddSignatureOpen(false);
      setNewSignature({ name: "", content: "", platforms: [], is_default: false, account_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear firma");
    }
  };

  const handleDeleteSignature = async (signatureId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/signatures/${signatureId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Firma eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Contact functions
  const handleAddContact = async () => {
    if (!newContact.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      const contactData = {
        ...newContact,
        tags: newContact.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      await axios.post(
        `${BACKEND_URL}/api/contacts`,
        contactData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contacto añadido");
      setAddContactOpen(false);
      setNewContact({
        name: "", role: "collaborator", email: "", phone: "",
        social_handles: {}, notes: "", tags: "", account_ids: []
      });
      fetchData();
    } catch (error) {
      toast.error("Error al añadir contacto");
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contacto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.message.trim()) {
      toast.error("Escribe un mensaje");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/contacts/message`,
        {
          contact_id: selectedContact.contact_id,
          message: newMessage.message,
          channel: newMessage.channel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mensaje guardado");
      setNewMessage({ message: "", channel: "note" });
      fetchContactMessages(selectedContact.contact_id);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar mensaje");
    }
  };

  const fetchContactMessages = async (contactId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/contacts/${contactId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContactMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const openMessageDialog = (contact) => {
    setSelectedContact(contact);
    setMessageDialogOpen(true);
    fetchContactMessages(contact.contact_id);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copiado");
  };

  const toggleSignaturePlatform = (platformId) => {
    setNewSignature(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const toggleContactAccount = (accountId) => {
    setNewContact(prev => ({
      ...prev,
      account_ids: prev.account_ids.includes(accountId)
        ? prev.account_ids.filter(a => a !== accountId)
        : [...prev.account_ids, accountId]
    }));
  };

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = !searchQuery || 
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.email || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === "all" || contact.role === selectedRole;
    const matchesAccount = !selectedAccount || contact.account_ids?.includes(selectedAccount);
    return matchesSearch && matchesRole && matchesAccount;
  });

  // Get signatures for an account
  const getSignaturesForAccount = (accountId) => {
    return signatures.filter(s => s.account_id === accountId || !s.account_id);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                <Users className="w-8 h-8 text-primary" />
                Cuentas y Contactos
              </h1>
              <p className="text-muted-foreground mt-1">
                Gestiona tus cuentas, firmas y contactos de colaboración
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Tabs defaultValue="accounts" className="space-y-6">
              <TabsList className="grid grid-cols-3 w-full max-w-md">
                <TabsTrigger value="accounts" data-testid="tab-accounts">
                  <Share2 className="w-4 h-4 mr-2" />
                  Cuentas
                </TabsTrigger>
                <TabsTrigger value="contacts" data-testid="tab-contacts">
                  <Users className="w-4 h-4 mr-2" />
                  Contactos
                </TabsTrigger>
                <TabsTrigger value="signatures" data-testid="tab-signatures">
                  <FileSignature className="w-4 h-4 mr-2" />
                  Firmas
                </TabsTrigger>
              </TabsList>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="space-y-6">
                {/* Filters and Add Button */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Buscar contactos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-contacts-input"
                    />
                  </div>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="w-40" data-testid="role-filter">
                      <SelectValue placeholder="Todos los roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {contactRoles.map(role => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.icon} {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-accent hover:bg-accent/90" data-testid="add-contact-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Añadir Contacto
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Añadir Contacto</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        <div>
                          <Label>Nombre *</Label>
                          <Input 
                            placeholder="Nombre del contacto"
                            value={newContact.name}
                            onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <Label>Rol</Label>
                          <Select 
                            value={newContact.role}
                            onValueChange={(v) => setNewContact({ ...newContact, role: v })}
                          >
                            <SelectTrigger data-testid="contact-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {contactRoles.map(role => (
                                <SelectItem key={role.id} value={role.id}>
                                  {role.icon} {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Email</Label>
                            <Input 
                              type="email"
                              placeholder="email@ejemplo.com"
                              value={newContact.email}
                              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>Teléfono</Label>
                            <Input 
                              placeholder="+34 600 000 000"
                              value={newContact.phone}
                              onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Redes Sociales</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {platformOptions.slice(0, 4).map(platform => (
                              <Input 
                                key={platform.id}
                                placeholder={`@${platform.id}`}
                                value={newContact.social_handles[platform.id] || ""}
                                onChange={(e) => setNewContact({ 
                                  ...newContact, 
                                  social_handles: { ...newContact.social_handles, [platform.id]: e.target.value }
                                })}
                              />
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label>Tags (separados por coma)</Label>
                          <Input 
                            placeholder="moda, lifestyle, madrid"
                            value={newContact.tags}
                            onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })}
                          />
                        </div>
                        {accounts.length > 0 && (
                          <div>
                            <Label>Asociar a cuentas</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {accounts.map(account => (
                                <div 
                                  key={account.account_id}
                                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                                    newContact.account_ids.includes(account.account_id)
                                      ? 'border-primary bg-primary/5'
                                      : 'border-border hover:border-primary/50'
                                  }`}
                                  onClick={() => toggleContactAccount(account.account_id)}
                                >
                                  <Checkbox 
                                    checked={newContact.account_ids.includes(account.account_id)}
                                    onChange={() => {}}
                                  />
                                  <span className="text-sm">{account.account_name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <Label>Notas</Label>
                          <Textarea 
                            placeholder="Notas sobre este contacto..."
                            value={newContact.notes}
                            onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                          />
                        </div>
                        <Button className="w-full" onClick={handleAddContact} data-testid="save-contact-btn">
                          Añadir Contacto
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Contacts Grid */}
                {filteredContacts.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No hay contactos</h3>
                      <p className="text-muted-foreground mb-6">
                        Añade contactos para gestionar tus colaboraciones
                      </p>
                      <Button className="gradient-neural glow-neural text-white" onClick={() => setAddContactOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Añadir Contacto
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredContacts.map((contact) => (
                      <Card 
                        key={contact.contact_id}
                        className="hover:shadow-md transition-shadow"
                        data-testid={`contact-${contact.contact_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-12 h-12">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {contact.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h4 className="font-semibold">{contact.name}</h4>
                                <Badge className={`text-xs ${roleColors[contact.role]}`}>
                                  {roleIcons[contact.role]}
                                  <span className="ml-1 capitalize">{contact.role}</span>
                                </Badge>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => openMessageDialog(contact)}>
                                  <MessageSquare className="w-4 h-4 mr-2" />
                                  Enviar mensaje
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteContact(contact.contact_id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            {contact.email && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                <span className="truncate">{contact.email}</span>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6"
                                  onClick={() => copyToClipboard(contact.email)}
                                >
                                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </Button>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{contact.phone}</span>
                              </div>
                            )}
                            {Object.keys(contact.social_handles || {}).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {Object.entries(contact.social_handles).map(([platform, handle]) => (
                                  handle && (
                                    <Badge key={platform} variant="outline" className="text-xs">
                                      {handle}
                                    </Badge>
                                  )
                                ))}
                              </div>
                            )}
                            {contact.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {contact.tags.map((tag, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {contact.last_contact && (
                            <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              Último contacto: {format(new Date(contact.last_contact), "PP", { locale: es })}
                            </div>
                          )}
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full mt-3"
                            onClick={() => openMessageDialog(contact)}
                            data-testid={`message-${contact.contact_id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Comunicación rápida
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Signatures Tab */}
              <TabsContent value="signatures" className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold">Firmas de Contenido</h2>
                    <p className="text-sm text-muted-foreground">
                      Firmas automáticas para añadir a tus publicaciones
                    </p>
                  </div>
                  <Dialog open={addSignatureOpen} onOpenChange={setAddSignatureOpen}>
                    <DialogTrigger asChild>
                      <Button className="gradient-neural glow-neural text-white" data-testid="add-signature-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nueva Firma
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Crear Firma</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Nombre de la firma</Label>
                          <Input 
                            placeholder="Ej: Firma profesional"
                            value={newSignature.name}
                            onChange={(e) => setNewSignature({ ...newSignature, name: e.target.value })}
                            data-testid="signature-name-input"
                          />
                        </div>
                        <div>
                          <Label>Contenido de la firma</Label>
                          <Textarea 
                            placeholder="Ej: 📧 contacto@miempresa.com | 🌐 www.miempresa.com"
                            value={newSignature.content}
                            onChange={(e) => setNewSignature({ ...newSignature, content: e.target.value })}
                            className="min-h-[100px]"
                            data-testid="signature-content-input"
                          />
                        </div>
                        <div>
                          <Label>Plataformas</Label>
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            {platformOptions.map(platform => (
                              <div 
                                key={platform.id}
                                className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                                  newSignature.platforms.includes(platform.id)
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => toggleSignaturePlatform(platform.id)}
                              >
                                <Checkbox 
                                  checked={newSignature.platforms.includes(platform.id)}
                                  onChange={() => {}}
                                />
                                <span className={`p-1 rounded text-white ${platform.color}`}>
                                  {platform.icon}
                                </span>
                                <span className="text-sm">{platform.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="default-signature"
                            checked={newSignature.is_default}
                            onCheckedChange={(checked) => setNewSignature({ ...newSignature, is_default: checked })}
                          />
                          <Label htmlFor="default-signature">Usar como firma predeterminada</Label>
                        </div>
                        <Button className="w-full" onClick={handleAddSignature} data-testid="save-signature-btn">
                          Crear Firma
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {signatures.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <FileSignature className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No hay firmas</h3>
                      <p className="text-muted-foreground mb-6">
                        Crea firmas para añadirlas automáticamente a tus posts
                      </p>
                      <Button onClick={() => setAddSignatureOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Firma
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {signatures.map((signature) => (
                      <Card key={signature.signature_id} data-testid={`signature-${signature.signature_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold flex items-center gap-2">
                                {signature.name}
                                {signature.is_default && (
                                  <Badge variant="secondary" className="text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Predeterminada
                                  </Badge>
                                )}
                              </h4>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteSignature(signature.signature_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="p-3 bg-secondary/30 rounded-lg mb-3">
                            <p className="text-sm whitespace-pre-wrap">{signature.content}</p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {signature.platforms?.map(platform => {
                              const platformInfo = platformOptions.find(p => p.id === platform);
                              return (
                                <span 
                                  key={platform}
                                  className={`p-1 rounded text-white text-xs ${platformInfo?.color || 'bg-gray-500'}`}
                                >
                                  {platformInfo?.icon}
                                </span>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Accounts Tab */}
              <TabsContent value="accounts" className="space-y-6">
                <SocialAccountsManager 
                  token={token} 
                  onAccountsChange={(newAccounts) => setAccounts(newAccounts)}
                />
              </TabsContent>
            </Tabs>
          )}

          {/* Message Dialog */}
          <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Comunicación con {selectedContact?.name}
                </DialogTitle>
              </DialogHeader>
              {selectedContact && (
                <div className="space-y-4">
                  {/* Contact info */}
                  <div className="flex items-center gap-4 p-3 bg-secondary/30 rounded-lg">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {selectedContact.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{selectedContact.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                    </div>
                    {selectedContact.email && (
                      <a href={`mailto:${selectedContact.email}`}>
                        <Button variant="outline" size="sm">
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                      </a>
                    )}
                  </div>
                  
                  {/* New message */}
                  <div className="space-y-3">
                    <div>
                      <Label>Nuevo mensaje/nota</Label>
                      <Textarea 
                        placeholder="Escribe tu mensaje o nota..."
                        value={newMessage.message}
                        onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                        className="min-h-[80px]"
                        data-testid="quick-message-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={newMessage.channel}
                        onValueChange={(v) => setNewMessage({ ...newMessage, channel: v })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="note">📝 Nota</SelectItem>
                          <SelectItem value="email">📧 Email</SelectItem>
                          <SelectItem value="reminder">⏰ Recordatorio</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button className="flex-1" onClick={handleSendMessage} data-testid="send-message-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                    </div>
                  </div>
                  
                  {/* Message history */}
                  {contactMessages.length > 0 && (
                    <div>
                      <Label className="mb-2 block">Historial</Label>
                      <ScrollArea className="h-48">
                        <div className="space-y-2">
                          {contactMessages.map((msg) => (
                            <div 
                              key={msg.message_id}
                              className="p-3 bg-secondary/30 rounded-lg text-sm"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <Badge variant="outline" className="text-xs">
                                  {msg.channel === 'note' ? '📝' : msg.channel === 'email' ? '📧' : '⏰'} {msg.channel}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(msg.created_at), "PP", { locale: es })}
                                </span>
                              </div>
                              <p>{msg.message}</p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};
